import React, { useState, useEffect, useCallback } from "react";
import api from "../api/client";

const C = {
  accent: "#345479", green: "#2d6a4f", red: "#9b2226", border: "#D9D3CC",
  card: "#F5F2EF", bg: "#ECE7E2", charcoal: "#333332",
};

const fmt = (v) => v ? `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "—";

/**
 * CapitalSourcesPanel — reusable component for managing capital sources.
 *
 * Props:
 *   dealId       - show sources for a specific deal (deal detail view)
 *   lpId         - show sources for a specific LP (investor detail view)
 *   investmentId - show sources for a specific deal_investment
 *   deals        - array of all deals (for dropdowns)
 *   investments  - array of deal_investments (for dropdowns)
 *   compact      - if true, use smaller layout
 */
export default function CapitalSourcesPanel({ dealId, lpId, investmentId, deals: dealsProp, investments: investmentsProp, compact }) {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deals, setDeals] = useState(dealsProp || []);
  const [investments, setInvestments] = useState(investmentsProp || []);
  const [bankFeedTxns, setBankFeedTxns] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [entities, setEntities] = useState([]);
  const [dealTeamMembers, setDealTeamMembers] = useState([]);
  const [error, setError] = useState("");

  function emptyForm() {
    return {
      deal_investment: investmentId || "",
      source_type: "fresh",
      amount: "",
      funding_date: "",
      source_deal: "",
      bank_feed_transaction_id: "",
      fronted_by: "",
      carry_recipient: "",
      is_repaid: false,
      repaid_date: "",
      repaid_amount: "",
      notes: "",
    };
  }

  const loadSources = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page_size: 500 };
      if (dealId) params.deal_id = dealId;
      if (lpId) params.lp_id = lpId;
      if (investmentId) params.investment_id = investmentId;
      const res = await api.get("/investors/capital-sources/", { params });
      setSources(res.data.results || res.data || []);
    } catch { setSources([]); }
    setLoading(false);
  }, [dealId, lpId, investmentId]);

  useEffect(() => { loadSources();
    api.get('/core/entities/').then(r => setEntities(r.data.results || r.data || [])).catch(() => {});
    api.get('/securities/deal-team/').then(r => setDealTeamMembers((r.data.results || r.data || []).filter(m => m.is_active !== false))).catch(() => {}); }, [loadSources]);

  useEffect(() => {
    if (!dealsProp) {
      api.get("/securities/deals/", { params: { page_size: 500 } })
        .then(r => setDeals(r.data.results || r.data || []))
        .catch(() => {});
    }
  }, [dealsProp]);

  useEffect(() => {
    if (!investmentsProp && dealId) {
      api.get("/investors/investments/", { params: { deal: dealId, page_size: 500 } })
        .then(r => setInvestments(r.data.results || r.data || []))
        .catch(() => {});
    } else if (!investmentsProp && lpId) {
      api.get("/investors/investments/", { params: { lp_account: lpId, page_size: 500 } })
        .then(r => setInvestments(r.data.results || r.data || []))
        .catch(() => {});
    }
  }, [investmentsProp, dealId, lpId]);

  // Load bank feed transactions for linking
  useEffect(() => {
    api.get("/reconciliation/bank-feed/", { params: { page_size: 5000 } })
      .then(r => {
        const items = (r.data.results || r.data || [])
          .filter(t => t.kind !== "internalTransfer" && t.amount)
          .sort((a, b) => (b.posted_date || "").localeCompare(a.posted_date || ""));
        setBankFeedTxns(items);
      })
      .catch(() => {});
  }, []);

  const handleSave = async () => {
    setSaving(true); setError("");
    try {
      const payload = {};
      Object.entries(form).forEach(([k, v]) => {
        if (v !== "" && v !== null && v !== undefined) payload[k] = v;
      });
      if (!payload.deal_investment) { setError("Select an investor allocation"); setSaving(false); return; }
      if (!payload.amount) { setError("Enter an amount"); setSaving(false); return; }

      if (editingId) {
        await api.patch(`/investors/capital-sources/${editingId}/`, payload);
      } else {
        await api.post("/investors/capital-sources/", payload);
      }
      setShowAdd(false);
      setEditingId(null);
      setForm(emptyForm());
      loadSources();
    } catch (e) {
      setError(e.response?.data ? JSON.stringify(e.response.data) : "Save failed");
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this capital source record?")) return;
    try {
      await api.delete(`/investors/capital-sources/${id}/`);
      loadSources();
    } catch (e) { alert("Delete failed: " + (e.response?.data?.detail || e.message)); }
  };

  const startEdit = (src) => {
    setForm({
      deal_investment: src.deal_investment || "",
      source_type: src.source_type || "fresh",
      amount: src.amount || "",
      funding_date: src.funding_date || "",
      source_deal: src.source_deal || "",
      bank_feed_transaction_id: src.bank_feed_transaction_id || "",
      fronted_by: src.fronted_by || "",
      carry_recipient: src.carry_recipient || "",
      is_repaid: src.is_repaid || false,
      repaid_date: src.repaid_date || "",
      repaid_amount: src.repaid_amount || "",
      notes: src.notes || "",
    });
    setEditingId(src.id);
    setShowAdd(true);
    setError("");
  };

  // Group sources by investment
  const grouped = {};
  sources.forEach(s => {
    const key = s.deal_investment;
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(s);
  });

  // Summary totals — external_recycled is bucketed under Recycled because
  // semantically it's the same: prior deal profits being redeployed.
  // promote_reinvested gets its own bucket — it's individual deal-team
  // carry, not LP- or mgmt-co-level.
  const totalFresh = sources.filter(s => s.source_type === "fresh").reduce((sum, s) => sum + Number(s.amount || 0), 0);
  const totalRecycled = sources.filter(s => ["recycled", "external_recycled"].includes(s.source_type)).reduce((sum, s) => sum + Number(s.amount || 0), 0);
  const totalFronted = sources.filter(s => s.source_type === "fronted").reduce((sum, s) => sum + Number(s.amount || 0), 0);
  const totalDirectPayment = sources.filter(s => s.source_type === "direct_payment").reduce((sum, s) => sum + Number(s.amount || 0), 0);
  const totalRetained = sources.filter(s => s.source_type === "retained").reduce((sum, s) => sum + Number(s.amount || 0), 0);
  const totalDistribution = sources.filter(s => s.source_type === "distribution").reduce((sum, s) => sum + Number(s.amount || 0), 0);
  const totalPromote = sources.filter(s => s.source_type === "promote_reinvested").reduce((sum, s) => sum + Number(s.amount || 0), 0);

  const labelStyle = { fontSize: 11, color: "#64748b", fontWeight: 500 };
  const valStyle = { fontSize: 13, fontWeight: 600 };
  const badgeColors = {
    fresh: "#2d6a4f", recycled: "#7c3aed", fronted: "#b45309",
    direct_payment: "#0369a1", retained: "#475569",
    distribution: "#9333ea", external_recycled: "#7c3aed",
    promote_reinvested: "#c2410c",
  };
  const badgeLabels = {
    fresh: "Fresh", recycled: "Recycled", fronted: "Fronted",
    direct_payment: "Direct Payment", retained: "Retained",
    distribution: "Dist. Reinvested", external_recycled: "Recycled (JV/Pool)",
    promote_reinvested: "Promote Reinvested",
  };

  return (
    <div style={{ marginTop: 8 }}>
      {/* Summary bar */}
      {sources.length > 0 && (
        <div style={{ display: "flex", gap: 16, marginBottom: 12, padding: "8px 12px", background: "#f8fafc", borderRadius: 8, border: "1px solid #e2e8f0", flexWrap: "wrap" }}>
          <div><span style={labelStyle}>Fresh Capital </span><span style={{ ...valStyle, color: C.green }}>{fmt(totalFresh)}</span></div>
          <div><span style={labelStyle}>Recycled </span><span style={{ ...valStyle, color: "#7c3aed" }}>{fmt(totalRecycled)}</span></div>
          <div><span style={labelStyle}>Fronted </span><span style={{ ...valStyle, color: "#b45309" }}>{fmt(totalFronted)}</span></div>
          {totalRetained > 0 && <div><span style={labelStyle}>Retained </span><span style={{ ...valStyle, color: "#475569" }}>{fmt(totalRetained)}</span></div>}
          {totalDistribution > 0 && <div><span style={labelStyle}>Dist. Reinvested </span><span style={{ ...valStyle, color: "#9333ea" }}>{fmt(totalDistribution)}</span></div>}
          {totalPromote > 0 && <div><span style={labelStyle}>Promote Reinvested </span><span style={{ ...valStyle, color: "#c2410c" }}>{fmt(totalPromote)}</span></div>}
          {totalDirectPayment > 0 && <div><span style={labelStyle}>Direct Payment </span><span style={{ ...valStyle, color: "#0369a1" }}>{fmt(totalDirectPayment)}</span></div>}
          <div><span style={labelStyle}>Total </span><span style={valStyle}>{fmt(totalFresh + totalRecycled + totalFronted + totalDirectPayment + totalRetained + totalDistribution + totalPromote)}</span></div>
        </div>
      )}

      {/* Source records */}
      {loading ? (
        <div style={{ padding: 12, color: "#94a3b8", fontSize: 12 }}>Loading capital sources...</div>
      ) : sources.length === 0 && !showAdd ? (
        <div style={{ padding: 12, color: "#94a3b8", fontSize: 12 }}>No capital sources recorded yet. Click below to add how each allocation was funded.</div>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
          <thead>
            <tr style={{ borderBottom: "2px solid " + C.border }}>
              {!investmentId && <th style={thStyle}>Investor</th>}
              {!dealId && <th style={thStyle}>Deal</th>}
              <th style={thStyle}>Type</th>
              <th style={{ ...thStyle, textAlign: "right" }}>Amount</th>
              <th style={thStyle}>Source Deal</th>
              <th style={thStyle}>Date</th>
              <th style={thStyle}>Mercury Txn</th>
              <th style={thStyle}>Notes</th>
              <th style={{ ...thStyle, width: 70 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sources.map(s => (
              <tr key={s.id} style={{ borderBottom: "1px solid #e2e8f0" }}>
                {!investmentId && <td style={tdStyle}>{s.lp_name || "—"}</td>}
                {!dealId && <td style={tdStyle}>{s.deal_name || "—"}</td>}
                <td style={tdStyle}>
                  <span style={{
                    padding: "2px 8px", borderRadius: 10, fontSize: 10, fontWeight: 600,
                    background: badgeColors[s.source_type] + "18",
                    color: badgeColors[s.source_type],
                  }}>
                    {badgeLabels[s.source_type] || s.source_type}
                  </span>
                  {s.source_type === "fronted" && s.fronted_by && (
                    <span style={{ marginLeft: 4, fontSize: 10, color: "#64748b" }}>by {s.fronted_by}</span>
                  )}
                  {s.source_type === "fronted" && s.is_repaid && (
                    <span style={{ marginLeft: 4, fontSize: 10, color: C.green }}>✓ repaid</span>
                  )}
                  {s.source_type === "promote_reinvested" && s.carry_recipient && (
                    <span style={{ marginLeft: 4, fontSize: 10, color: "#64748b" }}>by {s.carry_recipient}</span>
                  )}
                </td>
                <td style={{ ...tdStyle, textAlign: "right", fontWeight: 600 }}>{fmt(s.amount)}</td>
                <td style={tdStyle}>
                  {s.source_deal_name || "—"}
                  {s.source_deal_envelope?.over_envelope && (
                    <div title={s.source_deal_envelope.warning} style={{
                      marginTop: 2, fontSize: 10, color: "#b45309", fontWeight: 600,
                    }}>
                      ⚠ over envelope (${Number(s.source_deal_envelope.lp_distributed_from_source_deal).toLocaleString()} distributed, ${Number(s.source_deal_envelope.lp_total_reinvested_from_source_deal).toLocaleString()} reinvested)
                    </div>
                  )}
                </td>
                <td style={tdStyle}>{s.funding_date || "—"}</td>
                <td style={tdStyle}>
                  {s.bank_feed_transaction_id ? (
                    <span style={{ fontSize: 10, color: C.accent, cursor: "pointer" }} title={s.bank_feed_transaction_id}>
                      🔗 Linked
                    </span>
                  ) : "—"}
                </td>
                <td style={{ ...tdStyle, maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.notes || "—"}</td>
                <td style={tdStyle}>
                  <button onClick={() => startEdit(s)} style={actionBtn("#2563eb")}>✎</button>
                  <button onClick={() => handleDelete(s.id)} style={actionBtn("#dc2626")}>✕</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Add / Edit form */}
      {showAdd ? (
        <div style={{ marginTop: 12, padding: 14, background: "#f0f9ff", borderRadius: 8, border: "1px solid #bae6fd" }}>
          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 10 }}>
            {editingId ? "Edit Capital Source" : "Add Capital Source"}
          </div>
          {error && <div style={{ color: C.red, fontSize: 12, marginBottom: 8 }}>{error}</div>}

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            {!investmentId && (
              <Field label="Investor Allocation">
                <select value={form.deal_investment} onChange={e => setForm({ ...form, deal_investment: e.target.value })} style={inputStyle}>
                  <option value="">Select...</option>
                  {investments.map(inv => (
                    <option key={inv.id} value={inv.id}>
                      {inv.lp_account_name || inv.lp_account} — {inv.deal_name || inv.deal} ({fmt(inv.invested_amount)})
                    </option>
                  ))}
                </select>
              </Field>
            )}
            <Field label="Source Type">
              <select value={form.source_type} onChange={e => {
                const next = e.target.value;
                setForm(prev => ({
                  ...prev,
                  source_type: next,
                  // Clear stale Mercury link if switching to a type that can't carry one
                  bank_feed_transaction_id: ['fresh', 'fronted', 'recycled'].includes(next)
                    ? prev.bank_feed_transaction_id
                    : '',
                  // source_deal applies to recycled / distribution / external_recycled / promote_reinvested
                  source_deal: ['recycled', 'distribution', 'external_recycled', 'promote_reinvested'].includes(next)
                    ? prev.source_deal
                    : '',
                  // carry_recipient only applies to promote_reinvested
                  carry_recipient: next === 'promote_reinvested' ? prev.carry_recipient : '',
                }));
              }} style={inputStyle}>
                <option value="fresh">Fresh Capital</option>
                <option value="recycled">Recycled Profits</option>
                <option value="fronted">Fronted Capital</option>
                <option value="retained">Retained Earnings</option>
                <option value="distribution">Distribution Reinvested</option>
                <option value="promote_reinvested">Deal-Team Promote Reinvested</option>
                <option value="direct_payment">Direct Payment</option>
                <option value="external_recycled">Externally-held Recycled (JV/Pool)</option>
              </select>
            </Field>
            <Field label="Amount ($)">
              <input type="number" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} style={inputStyle} step="0.01" placeholder="0.00" />
            </Field>
            <Field label="Funding Date">
              <input type="date" value={form.funding_date} onChange={e => setForm({ ...form, funding_date: e.target.value })} style={inputStyle} />
            </Field>

            {(form.source_type === "recycled" || form.source_type === "distribution" || form.source_type === "external_recycled" || form.source_type === "promote_reinvested") && (
              <Field label={
                form.source_type === "distribution" ? "Source Deal (distribution from)"
                : form.source_type === "external_recycled" ? "Source Deal (held via JV/pool)"
                : form.source_type === "promote_reinvested" ? "Source Deal (carry from)"
                : "Source Deal (profits from)"
              }>
                <select value={form.source_deal} onChange={e => setForm({ ...form, source_deal: e.target.value })} style={inputStyle}>
                  <option value="">Select deal...</option>
                  {deals.map(d => (
                    <option key={d.id} value={d.id}>{d.deal_name}</option>
                  ))}
                </select>
              </Field>
            )}

            {form.source_type === "promote_reinvested" && (
              <Field label="Carry Recipient (deal-team member)">
                <select value={form.carry_recipient || ""} onChange={e => setForm({ ...form, carry_recipient: e.target.value })} style={inputStyle}>
                  <option value="">Select recipient...</option>
                  {dealTeamMembers.map(m => (
                    <option key={m.id} value={m.member_name}>{m.member_name}</option>
                  ))}
                </select>
                <div style={{ fontSize: 10, color: "#64748b", marginTop: 4 }}>
                  E.g. Jeremy. Posts <code>DR Investments:Fund Reinvestment / CR Retained Earnings:Promote:&lt;recipient&gt;</code> — no Mercury wire (synthetic).
                </div>
              </Field>
            )}

            {form.source_type === "external_recycled" && (
              <Field label="Held by (JV / pool entity)">
                <select value={form.fronted_by || ""} onChange={e => setForm({ ...form, fronted_by: e.target.value })} style={inputStyle}>
                  <option value="">Select holder...</option>
                  {/* Reusing the entity dropdown — the JV/pool should be modeled as an Entity */}
                  {entities.map(ent => (
                    <option key={ent.id} value={ent.name}>{ent.name}</option>
                  ))}
                </select>
                <div style={{ fontSize: 10, color: "#64748b", marginTop: 4 }}>
                  E.g. Quaestor JV, ArenaCo pool. Posts <code>CR Investments:&lt;source deal&gt;:via &lt;holder&gt;</code> on Helena's books.
                </div>
              </Field>
            )}

            {form.source_type === "direct_payment" && (
              <Field label="Who Paid">
                <select value={form.fronted_by} onChange={e => setForm({ ...form, fronted_by: e.target.value })} style={inputStyle}>
                  <option value="">-- Who paid --</option>
                  {entities.map(e => <option key={e.id} value={e.name}>{e.name}</option>)}
                </select>
              </Field>
            )}

            {form.source_type === "fronted" && (
              <>
                <Field label="Fronted By">
                <select value={form.fronted_by} onChange={e => setForm({ ...form, fronted_by: e.target.value })} style={inputStyle}><option value="">-- Fronted by --</option>{entities.map(e => <option key={e.id} value={e.name}>{e.name}</option>)}</select>
                </Field>
                <Field label="Repaid?">
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <input type="checkbox" checked={form.is_repaid} onChange={e => setForm({ ...form, is_repaid: e.target.checked })} />
                    {form.is_repaid && (
                      <>
                        <input type="date" value={form.repaid_date} onChange={e => setForm({ ...form, repaid_date: e.target.value })} style={{ ...inputStyle, width: 130 }} />
                        <input type="number" value={form.repaid_amount} onChange={e => setForm({ ...form, repaid_amount: e.target.value })} style={{ ...inputStyle, width: 100 }} placeholder="Amount" step="0.01" />
                      </>
                    )}
                  </div>
                </Field>
              </>
            )}

            {["fresh", "fronted", "recycled"].includes(form.source_type) && (
            <Field label="Link Mercury Transaction">
              <select value={form.bank_feed_transaction_id} onChange={e => setForm({ ...form, bank_feed_transaction_id: e.target.value })} style={inputStyle}>
                <option value="">None</option>
                {bankFeedTxns.slice(0, 200).map(t => (
                  <option key={t.mercury_txn_id} value={t.mercury_txn_id}>
                    {t.posted_date} | {t.counterparty_name} | ${Number(t.amount).toLocaleString()} | {t.kind}
                  </option>
                ))}
              </select>
            </Field>
            )}
            {form.source_type === "direct_payment" && (
              <Field label="Payment Info">
                <div style={{ fontSize: 11, color: "#64748b" }}>No Mercury transaction — investor paid counterparty directly.</div>
              </Field>
            )}
            {form.source_type === "retained" && (
              <Field label="Payment Info">
                <div style={{ fontSize: 11, color: "#64748b" }}>No Mercury link — retained earnings come from many historical fee/carry inflows already booked.</div>
              </Field>
            )}
            {form.source_type === "distribution" && (
              <Field label="Payment Info">
                <div style={{ fontSize: 11, color: "#64748b" }}>No Mercury link — the distribution is synthetic; no actual outbound wire occurred.</div>
              </Field>
            )}
            {form.source_type === "promote_reinvested" && (
              <Field label="Payment Info">
                <div style={{ fontSize: 11, color: "#64748b" }}>
                  No Mercury link — recipient's earned carry is converted to an investment without a cash leg.
                  Envelope check is informational until carry posts to the GL (CLAUDE.md follow-up #1) and the waterfall engine is wired.
                </div>
              </Field>
            )}
          </div>

          <Field label="Notes">
            <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} style={{ ...inputStyle, minHeight: 40 }} rows={2}
              placeholder={form.source_type === "direct_payment" ? 'e.g. "Wire sent directly to [Company] per investor direction"' : "Optional notes..."} />
          </Field>

          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
            <button onClick={handleSave} disabled={saving} style={{
              padding: "8px 20px", borderRadius: 6, border: "none",
              background: C.accent, color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer",
              opacity: saving ? 0.6 : 1,
            }}>
              {saving ? "Saving..." : editingId ? "Update" : "Save"}
            </button>
            <button onClick={() => { setShowAdd(false); setEditingId(null); setForm(emptyForm()); setError(""); }} style={{
              padding: "8px 16px", borderRadius: 6, border: "1px solid #94a3b8",
              background: "transparent", color: "#475569", fontSize: 13, cursor: "pointer",
            }}>
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button onClick={() => { setShowAdd(true); setForm(emptyForm()); setError(""); }} style={{
          marginTop: 8, padding: "6px 14px", borderRadius: 6,
          border: "1px solid " + C.accent, background: "transparent",
          color: C.accent, fontSize: 12, fontWeight: 600, cursor: "pointer",
        }}>
          + Add Capital Source
        </button>
      )}
    </div>
  );
}

/* ── tiny helpers ─────────────────────────────── */
function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 6 }}>
      <div style={{ fontSize: 11, fontWeight: 500, color: "#475569", marginBottom: 3 }}>{label}</div>
      {children}
    </div>
  );
}

const inputStyle = {
  width: "100%", padding: "6px 10px", borderRadius: 6,
  border: "1px solid #cbd5e1", fontSize: 12, boxSizing: "border-box",
};

const thStyle = { textAlign: "left", padding: "6px 8px", fontSize: 11, fontWeight: 600, color: "#64748b" };
const tdStyle = { padding: "6px 8px", fontSize: 12, color: "#334155" };

function actionBtn(color) {
  return {
    padding: "2px 6px", borderRadius: 4, border: `1px solid ${color}`,
    background: "transparent", color, fontSize: 11, cursor: "pointer", marginRight: 4,
  };
}
